// src/Dashboard.js
import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './styles/Dashboard.scss';
import logger from './utils/logger';
import { FaTrashAlt, FaCopy, FaCheck } from 'react-icons/fa';
import { track } from '@vercel/analytics';
import { getCategoryIcon } from './components/CategorySelection';
import { copyToClipboard } from './utils/copyToClipboard';

// Delete confirmation modal component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Delete Essay</h3>
        <p>Are you sure you want to delete this essay? This action cannot be undone.</p>
        <div className="modal-buttons">
          <button 
            className="modal-button cancel-button" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="modal-button confirm-button" 
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Add error modal component
const ErrorModal = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <p>{message}</p>
        <div className="modal-buttons">
          <button 
            className="modal-button cancel-button" 
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Map category codes to display names
export const getCategoryDisplayName = (category) => {
  if (!category) return 'Unknown';
  
  const categoryMap = {
    // English
    'Shakespeare': 'Shakespeare',
    'GCSE_Shakespeare': 'Shakespeare',
    'GCSE_19th': '19th Century Novel',
    'GCSE_Inspector': 'Modern Text',
    'GCSE_Poetry': 'Poetry',
    'Poetry': 'Poetry',
    'Comparative': 'Comparative',
    'Composition': 'Composition',
    
    // Irish
    'Aiste': 'Aiste',
    'Grammar Checker': 'Grammar & Spelling Checker',
    'IE_Oral': 'Oral Exam',
    'Filiocht_I': 'Filíocht - Cuid I',
    'Filiocht_II': 'Filíocht - Cuid II',
    'Filiocht_III': 'Filíocht - Cuid III',
    
    // French
    'FR_Oral': 'Oral Exam',
    'FR_Diary': 'Diary',
    'FR_Opinion': 'Opinion',
    'FR_Email': 'Email',
    'FR_Recit': 'Récit',
    'FR_Grammar': 'Grammar & Spelling Checker',
    
    // Spanish
    'ES_Oral': 'Oral Exam',
    'ES_Opinion': 'Opinion',
    'ES_LetterEmail': 'Letter/Email',
    'ES_Diary': 'Diary',
    'ES_Note': 'Note',
    'ES_Grammar': 'Grammar & Spelling Checker',
    
    // German
    'DE_Oral': 'Oral Exam',
    'DE_Ausserung': 'Äußerung',
    'DE_Letter': 'Letter',
    'DE_TopicalQuestions': 'Topical Questions',
    'DE_Grammar': 'Grammar & Spelling Checker',
    
    // Maths
    'MATH_Concepts': 'Concepts and Skills',
    'MATH_Applications': 'Contexts and Applications',

    // Science
    'Biology': 'Biology',
    'Chemistry': 'Chemistry'
  };
  
  return categoryMap[category] || category;
};

// Map category to subject (just like in App.js)
export const getCategorySubject = (category) => {
  if (!category) return 'Unknown';
  
  // Map category to subject with exact prefix matching
  if (category === 'Shakespeare' || category === 'Poetry' || 
      category === 'Comparative' || category === 'Composition' ||
      category.startsWith('GCSE_')) {
    return 'English';
  } else if (category === 'Aiste' || category === 'Grammar Checker' || 
            category === 'IE_Oral' || category.startsWith('Filiocht')) {
    return 'Irish';
  } else if (category.startsWith('FR_')) {
    return 'French';
  } else if (category.startsWith('ES_')) {
    return 'Spanish';
  } else if (category.startsWith('DE_')) {
    return 'German';
  } else if (category.startsWith('MATH_')) {
    return 'Maths';
  } else if (category.startsWith('GEO_')) {
    return 'Geography';
  } else if (category === 'Biology' || category === 'Chemistry') {
    return category; // Subject is the category itself
  } else {
    // Log unknown categories for debugging
    logger.warn(`Unknown category found: "${category}", defaulting to Unknown`);
    return 'Unknown'; // Changed default to make issues more visible
  }
};

function Dashboard({ authToken, onContinueEssay, onViewQuizAttempt }) {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [studentPage, setStudentPage] = useState(1);
  const [quizAttemptPage, setQuizAttemptPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreStudentEssays, setHasMoreStudentEssays] = useState(true);
  const [hasMoreQuizAttempts, setHasMoreQuizAttempts] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStudentEssays, setIsLoadingStudentEssays] = useState(false);
  const [isLoadingQuizAttempts, setIsLoadingQuizAttempts] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(() => {
    // Initialize with 'myEssays-main' expanded for students
    const initialSections = new Set();
    // We don't have userData yet, so we'll update this in useEffect
    return initialSections;
  });
  const [studentEssays, setStudentEssays] = useState([]);
  const { clearToken, userData, handleApiError } = useContext(AuthContext);
  const navigate = useNavigate();
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, essayId: null });
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [copiedAttemptShareId, setCopiedAttemptShareId] = useState(null);

  // Debug logging for userData and role
  useEffect(() => {
    logger.info('userData:', userData);
    logger.info('user role:', userData?.role);
  }, [userData]);

  // Add function to check if teacher exists for subject
  const hasTeacherForSubject = (subject) => {
    if (!userData?.teacher_email) return false;
    try {
      if (userData.teacher_email.startsWith('{')) {
        const teacherDict = JSON.parse(userData.teacher_email);
        return !!teacherDict[subject];
      }
      // Legacy format - only for English
      return subject === 'English' ? !!userData.teacher_email : false;
    } catch (e) {
      logger.error('Error parsing teacher email:', e);
      return false;
    }
  };

  const fetchDashboard = async (page) => {
    if (!authToken) {
      logger.info('No auth token found');
      setError('No authentication token found');
      navigate('/');
      return;
    }

    setIsLoading(true);
    try {
      logger.info("Fetching QA dashboard...");
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/qa-dashboard/${page}`,
        {
          method: 'GET',
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const responseText = await response.text();
      logger.info('Dashboard response:', {
        status: response.status,
        text: responseText
      });

      if (!response.ok) {
        const errorData = JSON.parse(responseText);
        if (errorData.code === 'SESSION_EXPIRED') {
          handleApiError(new Error(`Server error: ${responseText}`));
          return;
        }
        throw new Error(`Server error: ${responseText}`);
      }

      const data = JSON.parse(responseText);
      setRecords(prevRecords => 
        page === 1 ? data.essays : [...(prevRecords || []), ...data.essays]
      );
      setHasMore(data.has_more);
    } catch (err) {
      handleApiError(err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudentEssays = async (page) => {
    if (userData?.role !== 'teacher') return;
    setIsLoadingStudentEssays(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/student-essays/${page}`,
        {
          method: 'GET',
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const responseText = await response.text();
      if (!response.ok) {
        const errorData = JSON.parse(responseText);
        if (errorData.code === 'SESSION_EXPIRED') {
          handleApiError(new Error(`Server error: ${responseText}`));
          return;
        }
        throw new Error(`Server error: ${responseText}`);
      }

      const data = JSON.parse(responseText);
      setStudentEssays(prevEssays => 
        page === 1 ? data.essays : [...(prevEssays || []), ...data.essays]
      );
      setHasMoreStudentEssays(data.has_more);
    } catch (error) {
      handleApiError(error);
      setError(error.message);
    } finally {
      setIsLoadingStudentEssays(false);
    }
  };

  useEffect(() => {
    fetchDashboard(1);
  }, [authToken]);

  useEffect(() => {
    if (userData?.role === 'teacher') {
      fetchStudentEssays(1);
    }
  }, [userData?.role]);

  // Add debug logging for user role
  useEffect(() => {
    logger.info('Current user role:', userData?.role);
    fetchQuizAttempts(1);
  }, [userData?.role, authToken]);

  // Add debug logging for records state changes
  useEffect(() => {
    logger.info('Records state updated:', records);
  }, [records]);

  // Set initial section expansion based on role and student essays
  useEffect(() => {
    if (userData) {
      setExpandedSections(prevSections => {
        const newSections = new Set(prevSections);
        if (
          userData.role === 'student' || 
          (userData.role === 'teacher' && (!studentEssays || studentEssays.length === 0))
        ) {
          newSections.add('myEssays-main');
        }
        return newSections;
      });
    }
  }, [userData, studentEssays]);

  // Group records by subject first, then by date
  const groupedRecords = {};  // Initialize empty object for grouped records by subject
  const groupedStudentEssays = {};  // Initialize empty object for grouped student essays by subject
  const groupedQuizAttempts = {}; // For quiz attempts
  
  // Only process records if they exist and are in an array
  if (Array.isArray(records)) {
    // First, print out the records for debugging
    records.forEach(record => {
      const subject = getCategorySubject(record.category);
      logger.debug(`Categorizing: "${record.category}" → "${subject}"`);
    });
    
    records.forEach(record => {
      // Store display string and timestamp for proper sorting
      const recordDate = new Date(record.created_at);
      const date = recordDate.toLocaleDateString();
      
      // Determine the subject for this record's category
      const subject = getCategorySubject(record.category);
      
      // Initialize subject entry if doesn't exist
      if (!groupedRecords[subject]) {
        groupedRecords[subject] = {
          dates: {}
        };
      }
      
      // Initialize date if doesn't exist for this subject
      if (!groupedRecords[subject].dates[date]) {
        groupedRecords[subject].dates[date] = {
          timestamp: recordDate.getTime(),
          essays: []
        };
      }
      
      // Push the record to the appropriate subject and date
      groupedRecords[subject].dates[date].essays.push(record);
    });
  }

  // Group student essays by subject first, then by date
  if (Array.isArray(studentEssays)) {
    studentEssays.forEach(essay => {
      const essayDate = new Date(essay.created_at);
      const date = essayDate.toLocaleDateString();
      
      // Determine the subject for this essay's category
      const subject = getCategorySubject(essay.category);
      
      // Initialize subject entry if doesn't exist
      if (!groupedStudentEssays[subject]) {
        groupedStudentEssays[subject] = {
          dates: {}
        };
      }
      
      // Initialize date if doesn't exist for this subject
      if (!groupedStudentEssays[subject].dates[date]) {
        groupedStudentEssays[subject].dates[date] = {
          timestamp: essayDate.getTime(),
          essays: []
        };
      }
      
      // Push the essay to the appropriate subject and date
      groupedStudentEssays[subject].dates[date].essays.push(essay);
    });
  }

  // Group quiz attempts by subject, then by date
  if (Array.isArray(quizAttempts)) {
    quizAttempts.forEach(attempt => {
      const attemptDate = new Date(attempt.started_at);
      const date = attemptDate.toLocaleDateString();
      const subject = getCategorySubject(attempt.subject);

      if (!groupedQuizAttempts[subject]) {
        groupedQuizAttempts[subject] = { dates: {} };
      }
      if (!groupedQuizAttempts[subject].dates[date]) {
        groupedQuizAttempts[subject].dates[date] = {
          timestamp: attemptDate.getTime(),
          attempts: []
        };
      }
      // Share_id should already be on the attempt object from the API
      groupedQuizAttempts[subject].dates[date].attempts.push(attempt);
    });
  }

  // Helper function to sort subjects alphabetically
  const sortSubjects = (subjects) => {
    return subjects.sort((a, b) => a.localeCompare(b));
  };

  // Helper function to sort dates
  const sortDates = (dates, subjectData) => {
    return dates.sort((a, b) => {
      return subjectData.dates[b].timestamp - subjectData.dates[a].timestamp; // Most recent first
    });
  };

  const toggleSubject = (subject) => {
    const newExpandedSubjects = new Set(expandedSubjects);
    if (newExpandedSubjects.has(subject)) {
      newExpandedSubjects.delete(subject);
    } else {
      newExpandedSubjects.add(subject);
    }
    setExpandedSubjects(newExpandedSubjects);
  };

  const isSubjectExpanded = (subject) => {
    return expandedSubjects.has(subject);
  };

  const toggleDate = (subject, date) => {
    const dateKey = `${subject}-${date}`;
    const newExpandedDates = new Set(expandedDates);
    if (newExpandedDates.has(dateKey)) {
      newExpandedDates.delete(dateKey);
    } else {
      newExpandedDates.add(dateKey);
    }
    setExpandedDates(newExpandedDates);
  };

  const isDateExpanded = (subject, date) => {
    const dateKey = `${subject}-${date}`;
    return expandedDates.has(dateKey);
  };

  const toggleSection = (recordId, section) => {
    const sectionKey = `${recordId}-${section}`;
    const newExpandedSections = new Set(expandedSections);
    if (newExpandedSections.has(sectionKey)) {
      newExpandedSections.delete(sectionKey);
    } else {
      newExpandedSections.add(sectionKey);
    }
    setExpandedSections(newExpandedSections);
  };

  const isSectionExpanded = (recordId, section) => {
    return expandedSections.has(`${recordId}-${section}`);
  };

  const handleContinueEssay = (essay) => {
    // Track the action
    track('essay_continue', {
      category: essay.category || 'Shakespeare',
      is_draft: essay.is_draft,
      user_role: userData?.role || 'unknown'
    });
    
    const continueEssay = {
      question: essay.question_text,
      answer: essay.answer_text,
      isDraft: essay.is_draft,
      category: essay.category || 'Shakespeare',
      startAtStep: 4
    };
    
    // Save to localStorage
    localStorage.setItem('continueEssay', JSON.stringify(continueEssay));
    
    // Pass the data to the callback
    onContinueEssay(continueEssay);
  };

  const toggleVisibility = async (essayId, visible) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/essays/${essayId}/visibility`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ visible })
        }
      );

      if (!response.ok) throw new Error('Failed to update visibility');

      // Update local state
      setRecords(records.map(record => 
        record.id === essayId 
          ? { ...record, visible_to_teacher: visible }
          : record
      ));
    } catch (error) {
      logger.error('Error updating visibility:', error);
    }
  };

  const handleViewFeedback = (essay) => {
    logger.info('handleViewFeedback called with essay:', essay);
    
    // Track viewing feedback
    track('essay_view_feedback', {
      category: essay.category || 'Shakespeare',
      is_math: essay.category === 'MATH_Concepts' || essay.category === 'MATH_Applications',
      user_role: userData?.role || 'unknown'
    });
    
    // Check if this is a math category and ensure category is properly set
    const isMathCategory = essay.category === 'MATH_Concepts' || essay.category === 'MATH_Applications';
    logger.info('Is math category detected:', isMathCategory, 'Category:', essay.category);
    
    // Make sure the category is explicitly set to the math category value
    const mathCategory = isMathCategory ? essay.category : null;
    
    const continueEssay = {
      question: essay.question_text,
      answer: essay.answer_text,
      result: essay.feedback,
      category: mathCategory || essay.category || 'Shakespeare', // Ensure category is properly preserved
      startAtStep: isMathCategory ? 2 : 5, // Go directly to step 2 for math, 5 for others
      skipGrading: true,
      task_id: essay.task_id,
      essayId: essay.id,
      teacherFeedback: essay.teacher_feedback,
      isMathFeedback: isMathCategory // Flag to indicate this is math feedback
    };
    logger.info('Saving to localStorage for view feedback:', continueEssay);
    
    // Save to localStorage
    localStorage.setItem('continueEssay', JSON.stringify(continueEssay));
    
    // Call onContinueEssay with the essay data directly
    onContinueEssay(continueEssay);
  };

  const handleViewQuizFeedback = (attempt) => {
    if (onViewQuizAttempt) {
      track('quiz_view_feedback_dashboard', {
        quiz_id: attempt.quiz_id,
        attempt_id: attempt.attempt_id,
        subject: attempt.subject,
        user_role: userData?.role || 'unknown'
      });
      onViewQuizAttempt(attempt.quiz_id, attempt.attempt_id, attempt.subject);
    } else {
      logger.error("onViewQuizAttempt prop is not defined in Dashboard");
      alert("Could not load quiz feedback. Please try again later.");
    }
  };

  const handleCopyAttemptShareId = async (text, attemptId) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedAttemptShareId(attemptId);
      setTimeout(() => setCopiedAttemptShareId(null), 2000); // Reset after 2 seconds
      
      // Track quiz attempt share ID copy from dashboard
      track('quiz_attempt_share_id_copied_dashboard', {
        attempt_id: attemptId,
        share_id: text,
        user_role: userData?.role || 'unknown'
      });
    }
  };

  const handleShareWithTeacher = async (record) => {
    try {
      const subject = getCategorySubject(record.category);
      logger.info(`Attempting to share essay for subject: ${subject}`);
      // Check if there's a teacher for this subject
      const hasTeacher = hasTeacherForSubject(subject);
      logger.info(`Has teacher for ${subject}: ${hasTeacher}, teacher_email:`, userData?.teacher_email);
      
      if (!hasTeacher) {
        const errorMsg = `You don't have a teacher set for ${subject}. Connect a teacher in My Account to share your work with them.`;
        logger.info(`Setting error message: ${errorMsg}`);
        setError(errorMsg);
        return;
      }

      // Track the action (before actually sharing, to track intent)
      track('essay_share_with_teacher', {
        category: record.category || 'Shakespeare',
        is_draft: record.is_draft,
        user_role: userData?.role || 'unknown'
      });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/essays/${record.id}/visibility`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ visible: true })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to share essay with teacher');
      }

      // Update the local state to reflect the change
      setRecords(prevRecords => 
        prevRecords.map(r => 
          r.id === record.id 
            ? { ...r, visible_to_teacher: true }
            : r
        )
      );
    } catch (error) {
      logger.error('Error sharing essay:', error);
      setError('Failed to share essay with teacher');
    }
  };

  const showDeleteConfirmation = (essayId) => {
    setDeleteConfirmation({ show: true, essayId });
  };

  const hideDeleteConfirmation = () => {
    setDeleteConfirmation({ show: false, essayId: null });
  };

  const handleDeleteEssay = async (essayId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/essays/${essayId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete essay: ${errorText}`);
      }

      // Find the essay in records to get its category for tracking
      const deletedEssay = records?.find(record => record.id === essayId) || 
                          studentEssays?.find(essay => essay.id === essayId);
      
      // Track the deletion with Vercel Analytics
      track('essay_deleted', { 
        category: deletedEssay?.category || 'unknown',
        is_draft: deletedEssay?.is_draft || false,
        user_role: userData?.role || 'unknown',
        id: userData?.id || 'unknown'
      });

      // Update local state to remove the deleted essay
      setRecords(prevRecords => prevRecords.filter(record => record.id !== essayId));
      
      // If it's a teacher, also check if the essay is in studentEssays
      if (userData?.role === 'teacher') {
        setStudentEssays(prevEssays => prevEssays.filter(essay => essay.id !== essayId));
      }

      // Show success message
      alert('Essay deleted successfully');
    } catch (error) {
      logger.error('Error deleting essay:', error);
      alert(`Error: ${error.message}`);
    } finally {
      hideDeleteConfirmation();
    }
  };

  // Function to navigate to teacher comprehensions
  const navigateToTeacherComprehensions = () => {
    navigate('/teacher/comprehensions', { state: { category: 'Comprehension' } });
  };

  const fetchQuizAttempts = async (page) => {
    if (!authToken) return;

    setIsLoadingQuizAttempts(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/quiz/my/attempts?page=${page}`, // Add page parameter
        {
          headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch quiz attempts: ${response.status}`);
      }
      const data = await response.json();
      // Assuming API returns { attempts: [], has_more: true/false }
      setQuizAttempts(prevAttempts => 
        page === 1 ? data.attempts : [...prevAttempts, ...data.attempts]
      );
      setHasMoreQuizAttempts(data.has_more);
    } catch (err) {
      logger.error('Error fetching quiz attempts:', err);
      setError(err.message); // Set general error
    } finally {
      setIsLoadingQuizAttempts(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal 
        isOpen={deleteConfirmation.show}
        onClose={hideDeleteConfirmation}
        onConfirm={() => handleDeleteEssay(deleteConfirmation.essayId)}
      />

      {/* Error Modal */}
      <ErrorModal 
        message={error}
        onClose={() => setError(null)}
      />

      {/* Navigation Bar */}
      <nav className="dashboard-nav">
        <h1>My Dashboard</h1>
        <div>
          <Link 
            to="/"
            className="back-button dashboard-back-btn"
          >
            Go Back
          </Link>
        </div>
      </nav>

      {/* Info Message */}
      <div className="info-message">
        <p>Only your 50 most recent answers will load by default. To load more, select "Load More Essays" below.</p>
      </div>

      {/* Add Comprehension Submissions Section for Students */}
      {userData?.role === 'student' && (
        <div className="essay-section">
          <div
            onClick={() => navigate('/my-submissions')}
            className="section-header"
            style={{ cursor: 'pointer' }}
          >
            <h2>Comprehension Exercises</h2>
            <span>▶</span>
          </div>
        </div>
      )}

      {/* NEW: Add My Comprehensions Section for Teachers */}
      {userData?.role === 'teacher' && (
        <div className="essay-section">
          <div
            onClick={navigateToTeacherComprehensions}
            className="section-header"
            style={{ cursor: 'pointer' }}
          >
            <h2>My Comprehensions</h2>
            <span>▶</span>
          </div>
        </div>
      )}

      {/* My Essays Section */}
      <div className="essay-section">
        <div
          onClick={() => toggleSection('myEssays', 'main')}
          className="section-header"
        >
          <h2>
            My Essays ({records ? Object.values(groupedRecords).reduce((total, subject) => {
              return total + Object.values(subject.dates).reduce((subTotal, date) => subTotal + date.essays.length, 0);
            }, 0) : 0})
          </h2>
          <span>{isSectionExpanded('myEssays', 'main') ? '▼' : '▶'}</span>
        </div>

        {isSectionExpanded('myEssays', 'main') && (
          <div className="essay-grid">
            {sortSubjects(Object.keys(groupedRecords)).map(subject => (
              <div
                key={subject}
                className="subject-card"
              >
                <div
                  onClick={() => toggleSubject(subject)}
                  className="subject-card-header"
                >
                  <h2>
                    {subject} ({Object.values(groupedRecords[subject].dates).reduce((total, date) => total + date.essays.length, 0)} {Object.values(groupedRecords[subject].dates).reduce((total, date) => total + date.essays.length, 0) === 1 ? 'answer' : 'answers'})
                  </h2>
                  <span>
                    {isSubjectExpanded(subject) ? '▼' : '▶'}
                  </span>
                </div>

                {isSubjectExpanded(subject) && (
                  <div className="subject-dates">
                    {sortDates(Object.keys(groupedRecords[subject].dates), groupedRecords[subject]).map(date => (
                      <div key={`${subject}-${date}`} className="date-section">
                        <div
                          onClick={() => toggleDate(subject, date)}
                          className="date-header"
                        >
                          <h3>
                            {date} ({groupedRecords[subject].dates[date].essays.length} {groupedRecords[subject].dates[date].essays.length === 1 ? 'essay' : 'essays'})
                          </h3>
                          <span>
                            {isDateExpanded(subject, date) ? '▼' : '▶'}
                          </span>
                        </div>

                        {isDateExpanded(subject, date) && (
                          <div className="essay-date-section">
                            {groupedRecords[subject].dates[date].essays.map((record) => (
                              <div key={record.id} className="essay-card">
                                <div className="essay-card-header">
                                  <div className="category-info">
                                    <span className="category-icon">{getCategoryIcon(record.category)}</span>
                                    <strong>{getCategoryDisplayName(record.category)}</strong>
                                  </div>
                                  <button 
                                    className="delete-essay-button"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent event bubbling
                                      showDeleteConfirmation(record.id);
                                    }}
                                    title="Delete essay"
                                  >
                                    <FaTrashAlt />
                                  </button>
                                </div>

                                {/* Collapsible Question Section */}
                                <div
                                  onClick={() => toggleSection(record.id, 'question')}
                                  className="collapsible-header"
                                >
                                  <strong>Question</strong>
                                  <span>{isSectionExpanded(record.id, 'question') ? '▼' : '▶'}</span>
                                </div>
                                {isSectionExpanded(record.id, 'question') && (
                                  <p className="collapsible-content">
                                    {record.question_text}
                                  </p>
                                )}

                                {/* Collapsible Answer Section */}
                                <div
                                  onClick={() => toggleSection(record.id, 'answer')}
                                  className="collapsible-header"
                                >
                                  <strong>Answer</strong>
                                  <span>{isSectionExpanded(record.id, 'answer') ? '▼' : '▶'}</span>
                                </div>
                                {isSectionExpanded(record.id, 'answer') && (
                                  <p className="collapsible-content">
                                    {record.answer_text}
                                  </p>
                                )}

                                {/* Collapsible Teacher Feedback Section - only show if feedback exists */}
                                {record.teacher_feedback && (
                                  <>
                                    <div
                                      onClick={() => toggleSection(record.id, 'feedback')}
                                      className="collapsible-header"
                                    >
                                      <strong>Teacher Feedback</strong>
                                      <span>{isSectionExpanded(record.id, 'feedback') ? '▼' : '▶'}</span>
                                    </div>
                                    {isSectionExpanded(record.id, 'feedback') && (
                                      <p className="collapsible-content">
                                        {record.teacher_feedback}
                                      </p>
                                    )}
                                  </>
                                )}

                                <div className="essay-footer">
                                  Created: {new Date(record.created_at).toLocaleTimeString()}
                                </div>

                                {/* Add status and continue/revise button */}
                                <div className="essay-footer">
                                  <p className="status">
                                    Status: {record.is_draft ? 'Draft' : 'Graded'}
                                  </p>
                                  {record.is_draft ? (
                                    <button 
                                      onClick={() => handleContinueEssay(record)}
                                      className="continue-button"
                                    >
                                      Continue Writing
                                    </button>
                                  ) : (
                                    <div className="button-container">
                                      <button 
                                        onClick={() => handleViewFeedback(record)}
                                        className="view-feedback-button"
                                      >
                                        View Feedback
                                      </button>
                                      <button 
                                        onClick={() => handleContinueEssay(record)}
                                        className="revise-button"
                                      >
                                        Revise
                                      </button>
                                      {/* Add visibility toggle for students */}
                                      {userData?.role === 'student' && (
                                        <div className="share-toggle-container">
                                          {!record.is_draft && (
                                            <button
                                              onClick={() => handleShareWithTeacher(record)}
                                              disabled={record.visible_to_teacher}
                                              className="share-toggle-button"
                                            >
                                              {record.visible_to_teacher ? 'Sent to Teacher ✓' : 'Share with Teacher'}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Load More Essays Button */}
      {hasMore && (
        <div className="load-more-button-container">
          <button
            onClick={() => {
              // Track loading more essays
              track('dashboard_load_more', {
                page: currentPage + 1,
                user_role: userData?.role || 'unknown',
                content_type: 'my_essays'
              });
              
              setCurrentPage(prev => prev + 1);
              fetchDashboard(currentPage + 1);
            }}
            disabled={isLoading}
            className="load-more-button"
          >
            {isLoading ? 'Loading...' : 'Load More Essays'}
          </button>
        </div>
      )}

      {/* Student Essays Section */}
      {userData?.role === 'teacher' && studentEssays.length > 0 && (
        <div className="essay-section">
          <div
            onClick={() => toggleSection('studentEssays', 'main')}
            className="section-header"
          >
            <h2>
              Student Essays ({studentEssays.length})
            </h2>
            <span>{isSectionExpanded('studentEssays', 'main') ? '▼' : '▶'}</span>
          </div>

          {isSectionExpanded('studentEssays', 'main') && (
            <div className="essay-grid">
              {sortSubjects(Object.keys(groupedStudentEssays)).map(subject => (
                <div
                  key={subject}
                  className="subject-card"
                >
                  <div
                    onClick={() => toggleSubject(subject)}
                    className="subject-card-header"
                  >
                    <h2>
                      {subject} ({Object.values(groupedStudentEssays[subject].dates).reduce((total, date) => total + date.essays.length, 0)} {Object.values(groupedStudentEssays[subject].dates).reduce((total, date) => total + date.essays.length, 0) === 1 ? 'essay' : 'essays'})
                    </h2>
                    <span>
                      {isSubjectExpanded(subject) ? '▼' : '▶'}
                    </span>
                  </div>

                  {isSubjectExpanded(subject) && (
                    <div className="subject-dates">
                      {sortDates(Object.keys(groupedStudentEssays[subject].dates), groupedStudentEssays[subject]).map(date => (
                        <div key={`${subject}-${date}`} className="date-section">
                          <div
                            onClick={() => toggleDate(subject, date)}
                            className="date-header"
                          >
                            <h3>
                              {date} ({groupedStudentEssays[subject].dates[date].essays.length} {groupedStudentEssays[subject].dates[date].essays.length === 1 ? 'essay' : 'essays'})
                            </h3>
                            <span>
                              {isDateExpanded(subject, date) ? '▼' : '▶'}
                            </span>
                          </div>

                          {isDateExpanded(subject, date) && (
                            <div className="essay-date-section">
                              {groupedStudentEssays[subject].dates[date].essays.map(essay => (
                                <div
                                  key={essay.id}
                                  className="essay-card"
                                >
                                  <div className="essay-card-header">
                                    <div className="essay-header">
                                      <div className="category-info">
                                        <span className="category-icon">{getCategoryIcon(essay.category)}</span>
                                        <strong>{getCategoryDisplayName(essay.category)}</strong>
                                      </div>
                                      <strong>Student:</strong> {essay.student_username}
                                    </div>
                                    <button 
                                      className="delete-essay-button"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent event bubbling
                                        showDeleteConfirmation(essay.id);
                                      }}
                                      title="Delete essay"
                                    >
                                      <FaTrashAlt />
                                    </button>
                                  </div>
                                  
                                  {/* Collapsible Question Section */}
                                  <div
                                    onClick={() => toggleSection(essay.id, 'question')}
                                    className="collapsible-header"
                                  >
                                    <strong>Question</strong>
                                    <span>{isSectionExpanded(essay.id, 'question') ? '▼' : '▶'}</span>
                                  </div>
                                  {isSectionExpanded(essay.id, 'question') && (
                                    <p className="collapsible-content">
                                      {essay.question_text}
                                    </p>
                                  )}

                                  {/* Collapsible Answer Section */}
                                  <div
                                    onClick={() => toggleSection(essay.id, 'answer')}
                                    className="collapsible-header"
                                  >
                                    <strong>Answer</strong>
                                    <span>{isSectionExpanded(essay.id, 'answer') ? '▼' : '▶'}</span>
                                  </div>
                                  {isSectionExpanded(essay.id, 'answer') && (
                                    <p className="collapsible-content">
                                      {essay.answer_text}
                                    </p>
                                  )}

                                  {/* Collapsible Feedback Section - only show if feedback exists */}
                                  {essay.teacher_feedback && (
                                    <>
                                      <div
                                        onClick={() => toggleSection(essay.id, 'feedback')}
                                        className="collapsible-header"
                                      >
                                        <strong>Teacher Feedback</strong>
                                        <span>{isSectionExpanded(essay.id, 'feedback') ? '▼' : '▶'}</span>
                                      </div>
                                      {isSectionExpanded(essay.id, 'feedback') && (
                                        <p className="collapsible-content">
                                          {essay.teacher_feedback}
                                        </p>
                                      )}
                                    </>
                                  )}

                                  <div className="essay-footer">
                                    <div className="status">
                                      <strong>Status:</strong> {essay.is_draft ? 'Draft' : 'Graded'}
                                    </div>
                                    <div className="usage-indicator">
                                      Submitted: {new Date(essay.created_at).toLocaleDateString()}
                                    </div>
                                    {!essay.is_draft && (
                                      <button
                                        onClick={() => handleViewFeedback(essay)}
                                        className="view-feedback-button"
                                      >
                                        View Feedback
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Load More Student Essays Button */}
          {hasMoreStudentEssays && (
            <div className="load-more-button-container">
              <button
                onClick={() => {
                  // Track loading more student essays
                  track('dashboard_load_more', {
                    page: studentPage + 1,
                    user_role: userData?.role || 'unknown',
                    content_type: 'student_essays'
                  });
                  
                  setStudentPage(prev => prev + 1);
                  fetchStudentEssays(studentPage + 1);
                }}
                disabled={isLoadingStudentEssays}
                className="load-more-button"
              >
                {isLoadingStudentEssays ? 'Loading...' : 'Load More Student Essays'}
              </button>
            </div>
          )}
        </div>
      )}

      {(
        <div className="essay-section">
          <div
            onClick={() => toggleSection('myQuizzes', 'main')}
            className="section-header"
          >
            <h2>
              My Quizzes ({quizAttempts ? quizAttempts.length : 0})
            </h2>
            <span>{isSectionExpanded('myQuizzes', 'main') ? '▼' : '▶'}</span>
          </div>

          {isSectionExpanded('myQuizzes', 'main') && (
            <div className="essay-grid">
              {sortSubjects(Object.keys(groupedQuizAttempts)).map(subject => (
                <div key={`quiz-subject-${subject}`} className="subject-card">
                  <div
                    onClick={() => toggleSubject(`quiz-${subject}`)}
                    className="subject-card-header"
                  >
                    <h2>
                      {subject} ({Object.values(groupedQuizAttempts[subject].dates).reduce((total, date) => total + date.attempts.length, 0)} {Object.values(groupedQuizAttempts[subject].dates).reduce((total, date) => total + date.attempts.length, 0) === 1 ? 'attempt' : 'attempts'})
                    </h2>
                    <span>{isSubjectExpanded(`quiz-${subject}`) ? '▼' : '▶'}</span>
                  </div>

                  {isSubjectExpanded(`quiz-${subject}`) && (
                    <div className="subject-dates">
                      {sortDates(Object.keys(groupedQuizAttempts[subject].dates), groupedQuizAttempts[subject]).map(date => (
                        <div key={`quiz-${subject}-${date}`} className="date-section">
                          <div
                            onClick={() => toggleDate(`quiz-${subject}`, date)}
                            className="date-header"
                          >
                            <h3>
                              {date} ({groupedQuizAttempts[subject].dates[date].attempts.length} {groupedQuizAttempts[subject].dates[date].attempts.length === 1 ? 'attempt' : 'attempts'})
                            </h3>
                            <span>{isDateExpanded(`quiz-${subject}`, date) ? '▼' : '▶'}</span>
                          </div>

                          {isDateExpanded(`quiz-${subject}`, date) && (
                            <div className="essay-date-section">
                              {groupedQuizAttempts[subject].dates[date].attempts.map((attempt) => (
                                <div key={attempt.attempt_id} className="essay-card quiz-attempt-card">
                                  <div className="essay-card-header">
                                    <div className="category-info">
                                      <span className="category-icon">🏅</span> 
                                      <strong>{attempt.quiz_title}</strong>
                                    </div>
                                  </div>
                                  
                                  {/* Display Share ID if available - text only */}
                                  {attempt.share_id && (
                                    <p className="quiz-share-id-dashboard-textonly">
                                      Share ID: {attempt.share_id}
                                    </p>
                                  )}

                                  <p>Subject: {getCategoryDisplayName(attempt.subject)}</p>
                                  <p>
                                    Score: {attempt.status === 'completed' && attempt.max_score != null && attempt.percentage != null ? 
                                      `${attempt.score} / ${attempt.max_score} (${attempt.percentage}%)` : 
                                      (attempt.score != null ? 
                                        (attempt.max_score != null ? `${attempt.score} / ${attempt.max_score} (Processing...)` : `${attempt.score} (Processing...)`) : 
                                        'Not yet graded')}
                                  </p>
                                  {attempt.share_id && (
                                    <p className="share-id-info">
                                      Share ID: {attempt.share_id}
                                      <button 
                                        onClick={() => handleCopyAttemptShareId(attempt.share_id, attempt.attempt_id)}
                                        className="copy-button-inline"
                                        title="Copy Share ID"
                                      >
                                        {copiedAttemptShareId === attempt.attempt_id ? <FaCheck /> : <FaCopy />}
                                      </button>
                                    </p>
                                  )}
                                  <p>Date: {new Date(attempt.started_at).toLocaleDateString()}</p>
                                  <p>Status: {attempt.status}</p>
                                  
                                  <div className="essay-footer">
                                    {attempt.status === 'completed' ? (
                                      <button 
                                        onClick={() => handleViewQuizFeedback(attempt)}
                                        className="view-feedback-button"
                                      >
                                        View Feedback
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleViewQuizFeedback(attempt)}
                                        className="continue-button"
                                      >
                                        Continue Quiz
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Load More Quiz Attempts Button */}
          {hasMoreQuizAttempts && isSectionExpanded('myQuizzes', 'main') && (
            <div className="load-more-button-container">
              <button
                onClick={() => {
                  track('dashboard_load_more', {
                    page: quizAttemptPage + 1,
                    user_role: userData?.role || 'unknown',
                    content_type: 'quiz_attempts'
                  });
                  setQuizAttemptPage(prev => prev + 1);
                  fetchQuizAttempts(quizAttemptPage + 1);
                }}
                disabled={isLoadingQuizAttempts}
                className="load-more-button"
              >
                {isLoadingQuizAttempts ? 'Loading...' : 'Load More Quiz Attempts'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
